import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Col, Container, Form, Row, Spinner, Table } from 'react-bootstrap';
import axios from '../utils/api';

const DEFAULT_FILTERS = {
  search: '',
  message_type: '',
  channel: '',
  has_registered: '',
  is_unsubscribed: '',
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    dateStyle: 'medium',
    timeStyle: 'short',
    hour12: true,
  });
};

const AdminInviteManager = () => {
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [scheduleChannel, setScheduleChannel] = useState('email');
  const [scheduleAt, setScheduleAt] = useState('');
  const [scheduling, setScheduling] = useState(false);

  const queryParams = useMemo(() => {
    const params = {};
    if (filters.search.trim()) params.search = filters.search.trim();
    if (filters.message_type) params.message_type = filters.message_type;
    if (filters.channel) params.channel = filters.channel;
    if (filters.has_registered !== '') params.has_registered = filters.has_registered;
    if (filters.is_unsubscribed !== '') params.is_unsubscribed = filters.is_unsubscribed;
    return params;
  }, [filters]);

  const loadInvites = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get('/api/friend-invites/admin', { params: queryParams });
      setInvites(Array.isArray(response.data) ? response.data : []);
    } catch (loadError) {
      setError(loadError.response?.data?.detail || 'Failed to load invite list');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInvites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters((current) => ({ ...current, [name]: value }));
  };

  const applyFilters = async (event) => {
    event.preventDefault();
    await loadInvites();
  };

  const clearFilters = async () => {
    setFilters(DEFAULT_FILTERS);
    try {
      setLoading(true);
      setError('');
      const response = await axios.get('/api/friend-invites/admin', { params: {} });
      setInvites(Array.isArray(response.data) ? response.data : []);
    } catch (loadError) {
      setError(loadError.response?.data?.detail || 'Failed to load invite list');
    } finally {
      setLoading(false);
    }
  };

  const handleSchedule = async () => {
    setError('');
    setSuccess('');
    try {
      setScheduling(true);
      const payload = {
        channel: scheduleChannel,
        scheduled_for: scheduleAt || null,
        message_type: filters.message_type || null,
        filter_channel: filters.channel || null,
        search: filters.search.trim() || null,
        has_registered: filters.has_registered === '' ? null : filters.has_registered === 'true',
        is_unsubscribed: filters.is_unsubscribed === '' ? null : filters.is_unsubscribed === 'true',
      };
      const response = await axios.post('/api/friend-invites/schedule-jobs', payload);
      setSuccess(
        `Job ${response.data.job_id} scheduled for ${response.data.target_invite_count} invitee(s).`
      );
    } catch (scheduleError) {
      setError(scheduleError.response?.data?.detail || 'Failed to schedule communication job');
    } finally {
      setScheduling(false);
    }
  };

  return (
    <Container className="py-4">
      <Row className="justify-content-center">
        <Col lg={12}>
          <Card className="shadow-sm border-0 mb-4">
            <Card.Body>
              <h2 className="h4 mb-3">Invitee Communication Manager</h2>
              <p className="text-muted mb-3">
                Filter invitees and schedule communication jobs for Email, SMS, or WhatsApp channels.
              </p>

              {error && <Alert variant="danger">{error}</Alert>}
              {success && <Alert variant="success">{success}</Alert>}

              <Form onSubmit={applyFilters}>
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Search</Form.Label>
                      <Form.Control
                        type="text"
                        name="search"
                        value={filters.search}
                        onChange={handleFilterChange}
                        placeholder="Name, email, or phone"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group className="mb-3">
                      <Form.Label>Message Type</Form.Label>
                      <Form.Select name="message_type" value={filters.message_type} onChange={handleFilterChange}>
                        <option value="">All</option>
                        <option value="Join Portal">Join Portal</option>
                        <option value="Share product">Share product</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group className="mb-3">
                      <Form.Label>Channel</Form.Label>
                      <Form.Select name="channel" value={filters.channel} onChange={handleFilterChange}>
                        <option value="">All</option>
                        <option value="email">Email</option>
                        <option value="sms">SMS</option>
                        <option value="whatsapp">WhatsApp</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group className="mb-3">
                      <Form.Label>Registered</Form.Label>
                      <Form.Select name="has_registered" value={filters.has_registered} onChange={handleFilterChange}>
                        <option value="">All</option>
                        <option value="true">True</option>
                        <option value="false">False</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group className="mb-3">
                      <Form.Label>Unsubscribed</Form.Label>
                      <Form.Select name="is_unsubscribed" value={filters.is_unsubscribed} onChange={handleFilterChange}>
                        <option value="">All</option>
                        <option value="true">True</option>
                        <option value="false">False</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <div className="d-flex align-items-start gap-2 mb-2">
                  <Button type="submit" variant="primary" className="btn-uniform-height">Apply Filters</Button>
                  <Button type="button" variant="outline-secondary" className="btn-uniform-height" onClick={clearFilters}>Clear</Button>
                </div>
              </Form>
            </Card.Body>
          </Card>

          <Card className="shadow-sm border-0 mb-4">
            <Card.Body>
              <h3 className="h5 mb-3">Schedule Communication Job</h3>
              <Row>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Channel</Form.Label>
                    <Form.Select value={scheduleChannel} onChange={(event) => setScheduleChannel(event.target.value)}>
                      <option value="email">Email</option>
                      <option value="sms">SMS</option>
                      <option value="whatsapp">WhatsApp</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Schedule At (Optional)</Form.Label>
                    <Form.Control
                      type="datetime-local"
                      value={scheduleAt}
                      onChange={(event) => setScheduleAt(event.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={5} className="d-flex align-items-end">
                  <Button onClick={handleSchedule} disabled={scheduling || invites.length === 0}>
                    {scheduling ? 'Scheduling...' : `Schedule for ${invites.length} Invitee(s)`}
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <Card className="shadow-sm border-0">
            <Card.Body>
              <h3 className="h5 mb-3">Invitee List</h3>
              {loading ? (
                <div className="text-center py-3">
                  <Spinner animation="border" />
                </div>
              ) : invites.length === 0 ? (
                <Alert variant="info" className="mb-0">No invitees match current filters.</Alert>
              ) : (
                <Table responsive bordered hover>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Invited By</th>
                      <th>Registered</th>
                      <th>Type</th>
                      <th>Channel</th>
                      <th>Unsubscribed</th>
                      <th>Last Message</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invites.map((invite) => (
                      <tr key={invite.id}>
                        <td>{invite.friend_name}</td>
                        <td>{invite.friend_email || '-'}</td>
                        <td>{invite.friend_phone_number || '-'}</td>
                        <td>{invite.buyer_name || invite.buyer_email || '-'}</td>
                        <td>{invite.has_registered ? 'True' : 'False'}</td>
                        <td>{invite.message_type}</td>
                        <td>{invite.last_message_channel || '-'}</td>
                        <td>{invite.is_unsubscribed ? 'True' : 'False'}</td>
                        <td>{formatDateTime(invite.last_message_sent_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default AdminInviteManager;
